import React from 'react'
import { BOOKMAKER_LOGOS } from '../config/bookmakers'
import { playerImageUrlFromSelection as getPlayerImageUrl } from '../config/playerImages'
import { getMarketDisplay, formatDateTime, getEVClass } from './pickEmConfig'
import { TourMarker } from '../components/ToolTour'
import '../styles/app-mobile.css'

// One Pick'em pick as a mobile card. Used by PickEmMobile for every row and by
// the Pick'em tour (on desktop too), so the tour always shows the real card.
// `ev` and `threshold` are for the user's chosen leg count.

const parseSelection = (sel) => {
  const m = sel.match(/^(.+?)\s+(over|under)\s+(.+)$/i)
  if (!m) return { name: sel, dir: null, line: null }
  return { name: m[1], dir: m[2].toLowerCase(), line: m[3] }
}

const noop = () => {}

/**
 * markers: tour only, { market, pick, dabble, threshold, ev, add } -> number
 * children: rendered at the bottom of the card (the expanded odds, error tip)
 */
export default function PickEmCard({
  opp, ev, threshold, legCount, className = '', style, blurPick = false,
  inMulti = false, legIdx = -1, isExpanded = false, showButtons = true,
  onOpenDabble = noop, onMulti = noop, onExpand = noop, onHide = noop,
  markers = {}, children,
}) {
  const mark = (part, style) => markers[part] != null && <TourMarker n={markers[part]} style={style} />
  const anchor = (part) => (markers[part] != null ? { position: 'relative' } : null)
  const blurText = blurPick ? { filter: 'blur(5px)', userSelect: 'none' } : {}

  const { name, dir, line } = parseSelection(opp.selection)
  const lineParts = line ? line.match(/^([\d.]+)\s*(.*)$/) : null
  const num  = lineParts ? lineParts[1] : line
  const stat = lineParts ? lineParts[2] : ''

  return (
    <div className={`bcm-card${inMulti ? ' bcm-card-in-multi' : ''}${className ? ` ${className}` : ''}`} style={style}>

      {/* Market type strip (it clips its own overflow, so a marker sits beside the text inside it) */}
      <div className="bcm-card-type-strip">
        {markers.market != null ? (
          <span style={{ position: 'relative' }}>
            {getMarketDisplay(opp.market_type)}
            {mark('market', { top: '-4px', right: '-28px' })}
          </span>
        ) : getMarketDisplay(opp.market_type)}
      </div>

      {/* EV corner ribbon */}
      <div className={`bcm-ev-corner ${getEVClass(ev)}`}>
        {ev !== null ? `${ev > 0 ? '+' : ''}${ev.toFixed(2)}%` : '—'}
        {mark('ev', { top: '-8px', right: '-6px' })}
      </div>

      {/* Card top: name + badge + match */}
      <div className="bcm-card-top">
        <div className="bcm-avatar-wrap">
          <img
            src={getPlayerImageUrl(opp.selection, opp.sport)}
            alt=""
            className="bcm-avatar"
            onLoad={e  => { e.target.style.opacity = '1'; e.target.parentNode.classList.remove('bc-avatar-missing') }}
            onError={e => { e.target.style.opacity = '0'; e.target.parentNode.classList.add('bc-avatar-missing') }}
          />
        </div>
        <div className="bcm-card-name-block" style={{ paddingLeft: 0 }}>
          <div className="bcm-player-name">{name}</div>
          {dir && (
            <div
              className={`bc-sel-badge ${dir}`}
              style={{ ...(blurPick ? { filter: 'blur(5px)', userSelect: 'none', pointerEvents: 'none' } : {}), ...anchor('pick') }}
            >
              <span className="bc-sel-badge-dir">{dir.charAt(0).toUpperCase() + dir.slice(1)}</span>
              <span className="bc-sel-badge-num">{num}</span>
              {stat && <span className="bc-sel-badge-stat">{stat}</span>}
              {mark('pick', { top: '-2px', right: '-24px' })}
            </div>
          )}
          <div className="bcm-match-name">{opp.match}</div>
          <div className="bcm-match-name" style={{ opacity: 0.6, fontSize: '11px', marginTop: '2px' }}>{formatDateTime(opp.commence_time)}</div>
        </div>
      </div>

      {/* Card footer: Dabble logo + threshold odds */}
      <div className="bcm-card-footer">
        <div className="bcm-card-footer-left">
          <div className="bcm-footer-books" style={anchor('dabble')}>
            {BOOKMAKER_LOGOS['Dabble'] && (
              <img
                src={BOOKMAKER_LOGOS['Dabble']}
                alt="Dabble"
                className="bcm-footer-logo"
                onClick={onOpenDabble}
                style={{ cursor: opp.game_url ? 'pointer' : 'default' }}
              />
            )}
            {mark('dabble')}
          </div>
          <span
            className="bcm-footer-odds"
            style={{ ...blurText, ...anchor('threshold') }}
          >
            {threshold?.toFixed(3) ?? '—'}
            {mark('threshold', { top: '-4px', right: '-26px' })}
          </span>
        </div>
        <div className="bcm-card-footer-right">
          <span className="bcm-footer-stake-label" style={{ fontSize: '11px', color: '#64748b' }}>
            {legCount}L threshold
          </span>
        </div>
      </div>

      {/* Button strip */}
      {showButtons && (
        <div className="bcm-btn-strip">
          <button
            className={`bcm-strip-multi${inMulti ? ' in-multi' : ''}`}
            onClick={onMulti}
            style={anchor('add')}
          >
            {inMulti && <span>{legIdx + 1}</span>}
            {mark('add')}
          </button>
          <div className="bcm-strip-right">
            <button
              className={`bcm-strip-btn${isExpanded ? ' active' : ''}`}
              onClick={onExpand}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                {isExpanded ? <polyline points="18 15 12 9 6 15"/> : <polyline points="6 9 12 15 18 9"/>}
              </svg>
            </button>
            <button className="bcm-strip-btn hide-btn" onClick={onHide}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                <line x1="1" y1="1" x2="23" y2="23"/>
              </svg>
            </button>
          </div>
        </div>
      )}

      {children}
    </div>
  )
}
